import { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import FutureBoardProgress from "./FutureBoardProgress";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Icon from "@mui/material/Icon";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Stepper from "@mui/material/Stepper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  DEFAULT_DOMAIN_KEYS, LIMITS, buildUpdateInput, changeFutureCardStatus, draftFromCard, draftFromSnapshot, fromCardImages,
  deleteFutureImage, getFutureCard, getGoals, isDirty, normalizeTags, parseFutureBoardError, sameImages, selectionOf, textForClipboard,
  updateDraft, updateFutureCard, validateDraft,
  type CardDraft, type DraftErrorCode, type EditorImage, type FutureBoardFailure, type FutureCard, type FutureCardText,
  type GoalInfo, type UpdateFutureCardInput,
} from "@chronospark/shared";
import { useUnsavedChangesGuard } from "hooks/useUnsavedChangesGuard";
import AssistPanel from "./AssistPanel";
import FailureAlert from "./FailureAlert";
import ImagePicker from "./ImagePicker";
import RevisionDialog from "./RevisionDialog";
import UnsavedChangesDialog from "./UnsavedChangesDialog";

type Translate = (key: string, options?: object) => string;

interface Props {
  cardId: number;
  accessToken?: string;
  language: string;
  t: Translate;
  isPro: boolean;
  /** 開く段階（0〜3） */
  startStep?: number;
  onRequirePro: () => void;
  /** ワンシーンへ戻る（確認を経たあとに呼ばれる） */
  onDone: () => void;
  onFailure: (failure: FutureBoardFailure) => void;
  onNotice: (message: string) => void;
}

const STEP_KEYS = ["desire", "futureSelf", "scene", "photosAndWords"] as const;

function errorText(code: DraftErrorCode | undefined, t: Translate, max?: number): string | undefined {
  return code ? t(`futureBoard.validation.${code}`, { max }) : undefined;
}

// 編集（4段階）。段階は行き来でき、タイトル以外は飛ばせる。保存は、サーバーの応答を受けてから「保存済み」にする。
// 編集中の内容はメモリだけに置く（ストレージには書かない）。
function FutureCardEditor({ cardId, accessToken, language, t, isPro, startStep = 0, onRequirePro, onDone, onFailure, onNotice }: Props) {
  const [card, setCard] = useState<FutureCard | null>(null);
  const [base, setBase] = useState<CardDraft | null>(null);
  const [draft, setDraft] = useState<CardDraft | null>(null);
  const [baseItems, setBaseItems] = useState<EditorImage[]>([]);
  const [items, setItems] = useState<EditorImage[]>([]);
  const [goals, setGoals] = useState<GoalInfo[]>([]);
  const [step, setStep] = useState(Math.min(Math.max(startStep, 0), STEP_KEYS.length - 1));
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<FutureBoardFailure | null>(null);
  const [conflict, setConflict] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const dirty = Boolean(draft && base && (isDirty(draft, base) || !sameImages(items, baseItems)));
  const guard = useUnsavedChangesGuard(dirty);
  const validation = useMemo(() => (draft ? validateDraft(draft) : { ok: false, errors: {} }), [draft]);

  const apply = useCallback((next: FutureCard) => {
    const nextDraft = draftFromCard(next);
    const nextItems = fromCardImages(next.images ?? []);
    setCard(next);
    setBase(nextDraft);
    setDraft(nextDraft);
    setBaseItems(nextItems);
    setItems(nextItems);
  }, []);

  const load = useCallback(async () => {
    if (!accessToken) return;
    try {
      const result = await getFutureCard(accessToken, cardId);
      apply(result.card);
      setConflict(false);
      setFailure(null);
    } catch (error) {
      const parsed = parseFutureBoardError(error);
      setFailure(parsed);
      if (parsed.kind === "disabled" || parsed.kind === "unauthorized") onFailure(parsed);
    }
  }, [accessToken, cardId, apply, onFailure]);

  useEffect(() => {
    void load();
  }, [load]);

  // 関連ゴールの選択肢（なくても保存できる。取得に失敗しても、編集は続けられる）
  useEffect(() => {
    if (!accessToken) return;
    getGoals(accessToken).then(setGoals).catch(() => setGoals([]));
  }, [accessToken]);

  const set = (patch: Partial<CardDraft>) => setDraft(current => (current ? updateDraft(current, patch) : current));

  // 保存。成功したらサーバーの内容に切り替える。競合・失敗では、入力を残したままにする。
  const save = useCallback(async (showOnBoard = false): Promise<boolean> => {
    if (!accessToken || !card || !draft || !base) return false;
    setShowErrors(true);
    if (!validation.ok) return false;
    setSaving(true);
    setFailure(null);
    try {
      let current = card;
      const input = buildUpdateInput(draft, base, card.version);
      const imagesChanged = !sameImages(items, baseItems);
      if (input || imagesChanged) {
        const body: UpdateFutureCardInput = input ?? { expected_version: card.version };
        if (imagesChanged) body.images = selectionOf(items);
        current = (await updateFutureCard(accessToken, card.id, body)).card;
      }
      if (showOnBoard && current.status !== "visible") {
        const changed = await changeFutureCardStatus(accessToken, card.id, "visible", current.version);
        current = { ...current, ...changed.card, images: changed.card.images ?? current.images };
      }
      apply(current);
      onNotice(t("futureBoard.edit.saved"));
      return true;
    } catch (error) {
      const parsed = parseFutureBoardError(error);
      if (parsed.kind === "conflict") setConflict(true);
      else if (parsed.kind === "requires_pro") onRequirePro();
      else setFailure(parsed);
      if (parsed.kind === "unauthorized" || parsed.kind === "disabled") onFailure(parsed);
      return false;
    } finally {
      setSaving(false);
    }
  }, [accessToken, card, draft, base, items, baseItems, validation.ok, apply, onNotice, onRequirePro, onFailure, t]);

  const leave = () => guard.requestLeave(onDone);

  // 「破棄して離れる」：この編集で登録しただけの写真は、サーバーにも残さない（失敗しても、あとで自動で片づく）
  const discardAndLeave = () => {
    items.filter(item => item.fresh && item.id !== null).forEach(item => {
      if (accessToken && item.id !== null) void deleteFutureImage(accessToken, item.id).catch(() => undefined);
    });
    guard.proceed();
  };

  const saveAndLeave = async () => {
    if (await save()) guard.proceed();
  };

  const copyMine = async () => {
    if (!draft) return;
    try {
      await navigator.clipboard.writeText(textForClipboard(draft));
      onNotice(t("futureBoard.actions.copied"));
    } catch {
      // クリップボードを使えない環境では、内容は画面に残っているので、手で選んでコピーできる
    }
  };

  if (failure && !card) {
    return (
      <Box>
        <FailureAlert failure={failure} t={t} onReload={() => void load()} />
        <Button onClick={onDone} startIcon={<Icon>arrow_back</Icon>}>{t("futureBoard.scene.backToBoard")}</Button>
      </Box>
    );
  }
  if (!card || !draft || !base) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><FutureBoardProgress aria-label={t("Common.loading")} /></Box>;
  }

  const errors = showErrors ? validation.errors : {};
  const domainOptions = DEFAULT_DOMAIN_KEYS.map(key => t(`futureBoard.domains.${key}`));
  const last = STEP_KEYS.length - 1;

  const tagField = (field: "domains" | "sensations", label: string, help: string, options: string[]) => (
    <Autocomplete
      multiple
      freeSolo
      options={options}
      value={draft[field]}
      onChange={(_, value) => set({ [field]: normalizeTags(value as string[]) } as Partial<CardDraft>)}
      renderTags={(value, getTagProps) => value.map((option, index) => <Chip {...getTagProps({ index })} key={`${option}-${index}`} label={option} size="small" />)}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          error={Boolean(errors[field])}
          helperText={errors[field] ? errorText(errors[field], t, errors[field] === "too_many" ? LIMITS[field].count : LIMITS[field].length) : help}
        />
      )}
    />
  );

  const text = (field: "meaning" | "self_image" | "scene", label: string, help: string, max: number, rows = 4) => (
    <TextField
      fullWidth
      multiline
      minRows={rows}
      label={label}
      value={draft[field]}
      onChange={event => set({ [field]: event.target.value } as Partial<CardDraft>)}
      error={Boolean(errors[field])}
      helperText={errors[field] ? errorText(errors[field], t, max) : `${help}（${[...draft[field].trim()].length} / ${max}）`}
    />
  );

  const stepContent = [
    <Stack spacing={2.5} key="desire">
      <TextField
        required
        fullWidth
        label={t("futureBoard.edit.titleLabel")}
        value={draft.title}
        onChange={event => set({ title: event.target.value })}
        error={Boolean(errors.title)}
        helperText={errors.title ? errorText(errors.title, t, LIMITS.title.max) : `${t("futureBoard.edit.titleHelp")}（${[...draft.title.trim()].length} / ${LIMITS.title.max}）`}
      />
      {tagField("domains", t("futureBoard.edit.domainsLabel"), t("futureBoard.edit.domainsHelp", { max: LIMITS.domains.count, length: LIMITS.domains.length }), domainOptions)}
      {text("meaning", t("futureBoard.edit.meaningLabel"), t("futureBoard.edit.meaningHelp"), LIMITS.meaning)}
    </Stack>,
    <Stack spacing={2.5} key="futureSelf">
      {text("self_image", t("futureBoard.edit.selfImageLabel"), t("futureBoard.edit.selfImageHelp"), LIMITS.self_image, 5)}
    </Stack>,
    <Stack spacing={2.5} key="scene">
      {text("scene", t("futureBoard.edit.sceneLabel"), t("futureBoard.edit.sceneHelp"), LIMITS.scene, 6)}
      {tagField("sensations", t("futureBoard.edit.sensationsLabel"), t("futureBoard.edit.sensationsHelp", { max: LIMITS.sensations.count, length: LIMITS.sensations.length }), [])}
    </Stack>,
    <Stack spacing={3} key="photosAndWords">
      <ImagePicker
        items={items}
        onChange={setItems}
        accessToken={accessToken}
        language={language}
        t={t}
        isPro={isPro}
        onRequirePro={onRequirePro}
        onNotice={onNotice}
        disabled={saving}
      />
      <FormControl fullWidth>
        <InputLabel id="future-goal-label" shrink>{t("futureBoard.edit.goalLabel")}</InputLabel>
        <Select
          displayEmpty
          labelId="future-goal-label"
          label={t("futureBoard.edit.goalLabel")}
          value={draft.related_goal_id === null ? "" : String(draft.related_goal_id)}
          onChange={event => set({ related_goal_id: event.target.value === "" ? null : Number(event.target.value) })}
        >
          <MenuItem value="">{t("futureBoard.edit.goalNone")}</MenuItem>
          {goals.map(goal => (
            <MenuItem key={goal.id} value={String(goal.id)}>{goal.goal}</MenuItem>
          ))}
        </Select>
        <FormHelperText>{t("futureBoard.edit.goalHelp")}</FormHelperText>
      </FormControl>
    </Stack>,
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 1 }}>
        <Button onClick={leave} startIcon={<Icon>arrow_back</Icon>}>{t("futureBoard.edit.backToScene")}</Button>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            color={dirty ? "warning" : "success"}
            variant={dirty ? "filled" : "outlined"}
            label={dirty ? t("futureBoard.edit.unsavedBadge") : t("futureBoard.edit.savedBadge")}
            role="status"
          />
          <Button onClick={() => setHistoryOpen(true)} startIcon={<Icon>history</Icon>}>{t("futureBoard.edit.history")}</Button>
        </Stack>
      </Stack>

      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>{t("futureBoard.edit.title")}</Typography>

      {conflict && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Stack direction="row" spacing={0.5}>
              <Button color="inherit" size="small" onClick={() => void copyMine()}>{t("futureBoard.actions.copyMine")}</Button>
              <Button color="inherit" size="small" onClick={() => void load()}>{t("futureBoard.actions.reload")}</Button>
            </Stack>
          }
        >
          <strong>{t("futureBoard.conflict.title")}</strong>
          <br />
          {t("futureBoard.edit.conflictBody")}
        </Alert>
      )}
      {failure && <FailureAlert failure={failure} t={t} onClose={() => setFailure(null)} />}

      <Card sx={{ p: { xs: 2, sm: 3 } }}>
        <Stepper nonLinear activeStep={step} alternativeLabel={false} sx={{ mb: 3, flexWrap: "wrap", gap: 1 }}>
          {STEP_KEYS.map((key, index) => (
            <Step key={key} completed={false}>
              <StepButton onClick={() => setStep(index)} aria-current={step === index ? "step" : undefined}>
                {t(`futureBoard.edit.steps.${key}`)}
              </StepButton>
            </Step>
          ))}
        </Stepper>

        {stepContent[step]}

        {/* ヒントをもらう（本人が押したときだけ動く。候補は、「使う」を押すと入力欄に入るだけ） */}
        <AssistPanel
          step={step}
          cardId={card.id}
          draft={draft}
          accessToken={accessToken}
          language={language}
          t={t}
          onApply={(field, text) => set({ [field]: text } as Partial<CardDraft>)}
          stepOfField={field => ({ title: 0, meaning: 0, self_image: 1, scene: 2 }[field])}
          onGoto={setStep}
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" sx={{ mt: 3 }}>
          <Button variant="outlined" onClick={() => setStep(step - 1)} disabled={step === 0}>{t("futureBoard.edit.back")}</Button>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            {step < last && (
              <>
                <Button onClick={() => setStep(step + 1)}>{t("futureBoard.edit.skip")}</Button>
                <Button variant="contained" onClick={() => setStep(step + 1)}>{t("futureBoard.edit.next")}</Button>
              </>
            )}
          </Stack>
        </Stack>
      </Card>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button onClick={() => void save()} disabled={saving || !dirty} variant={card.status === "visible" ? "contained" : "outlined"}>
          {saving && <FutureBoardProgress size={18} color="inherit" />}{t("futureBoard.edit.save")}
        </Button>
        {card.status !== "visible" && (
          <Button onClick={() => void save(true)} disabled={saving} variant="contained">
            {t("futureBoard.edit.saveAndShow")}
          </Button>
        )}
      </Stack>

      <RevisionDialog
        open={historyOpen}
        cardId={card.id}
        currentVersion={card.version}
        accessToken={accessToken}
        language={language}
        t={t}
        onClose={() => setHistoryOpen(false)}
        onRestore={(snapshot: FutureCardText) => {
          set(draftFromSnapshot(snapshot, draft));
          setHistoryOpen(false);
          setStep(0);
          onNotice(t("futureBoard.history.restored"));
        }}
      />

      <UnsavedChangesDialog
        open={guard.isConfirming}
        busy={saving}
        canSave={validation.ok}
        t={t}
        onSaveLeave={() => void saveAndLeave()}
        onDiscard={discardAndLeave}
        onKeep={guard.cancel}
      />
    </Box>
  );
}

export default FutureCardEditor;
